import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  getErpBranchDetails,
  getErpRequestErrorMessage,
  isErpApiSuccess,
  postRojmel,
  setActiveBranchId,
} from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import "../assets/css/rojmel.css";

const MODES = [
  { key: "today_total", label: "Day Book" },
  { key: "day_work", label: "Day Work" },
  { key: "amount", label: "Amount" },
  { key: "gold_fine", label: "Gold Fine" },
  { key: "silver_fine", label: "Silver Fine" },
];

function todayIsoDate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function num(value, fallback = 0) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatAmount(value) {
  const n = num(value);
  if (n === 0) return "0.00";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatFine(value) {
  const n = num(value);
  if (n === 0) return "0.000";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function branchOptionsFromSession(preferredBranchId) {
  const list = getErpBranchDetails();
  const opts = (Array.isArray(list) ? list : [])
    .map((b) => {
      const id = b?.id ?? b?.branch_id ?? b?.branchId;
      if (id == null || id === "") return null;
      const name =
        b?.name ||
        b?.branch_name ||
        b?.branchName ||
        b?.label ||
        `Branch #${id}`;
      return { value: String(id), label: String(name) };
    })
    .filter(Boolean);

  if (
    preferredBranchId != null &&
    preferredBranchId !== "" &&
    !opts.some((o) => String(o.value) === String(preferredBranchId))
  ) {
    opts.unshift({
      value: String(preferredBranchId),
      label: `Branch #${preferredBranchId}`,
    });
  }
  return opts;
}

function valueColumnsForMode(mode) {
  if (mode === "amount") {
    return [{ key: "amount", label: "Amount", format: formatAmount }];
  }
  if (mode === "gold_fine") {
    return [{ key: "gold_fine", label: "Gold Fine", format: formatFine }];
  }
  if (mode === "silver_fine") {
    return [{ key: "silver_fine", label: "Silver Fine", format: formatFine }];
  }
  // day_work + today_total (Day Book): amount + metal fines
  return [
    { key: "amount", label: "Amount", format: formatAmount },
    { key: "gold_fine", label: "Gold Fine", format: formatFine },
    { key: "silver_fine", label: "Silver Fine", format: formatFine },
  ];
}

function isOpeningBalanceRow(row) {
  const book = String(row?.book || "").toUpperCase();
  const name = String(row?.name || "").toLowerCase();
  return book === "OPBAL" || name.includes("opening balance");
}

function pickGmAmount(value) {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return {
      gram: num(value.gram ?? value.gm ?? value.weight),
      amount: num(value.amount ?? value.amt ?? value.total),
    };
  }
  return { gram: 0, amount: num(value) };
}

function formatGmAndAmount(value) {
  const { gram, amount } = pickGmAmount(value);
  return `${formatFine(gram)} gm / ${formatAmount(amount)}`;
}

const DAY_BOOK_ROWS = [
  { key: "total_bill", label: "Total Bill", kind: "amount" },
  { key: "total_gold", label: "Total Gold", kind: "amount" },
  { key: "total_silver", label: "Total Silver", kind: "amount" },
  { key: "total_barcode", label: "Total Barcode", kind: "amount" },
  { key: "total_cash", label: "Total Cash", kind: "amount" },
  { key: "total_bank", label: "Total Bank", kind: "amount" },
  { key: "total_card", label: "Total Card", kind: "amount" },
  { key: "total_old_gold", label: "Total Old Gold", kind: "gm_amount" },
  { key: "total_old_silver", label: "Total Old Silver", kind: "gm_amount" },
  { key: "total_sale", label: "Total Sale", kind: "amount" },
  { key: "total_gst", label: "Total GST", kind: "amount" },
];

function readDayBookField(summary, row) {
  if (!summary || typeof summary !== "object") return null;
  if (summary[row.key] != null && summary[row.key] !== "") {
    return summary[row.key];
  }
  for (const alt of row.altKeys || []) {
    if (summary[alt] != null && summary[alt] !== "") return summary[alt];
  }
  return null;
}

function DayBookSummaryTable({ summary, loading, emptyMessage }) {
  const hasSummary =
    summary != null &&
    typeof summary === "object" &&
    Object.keys(summary).length > 0;

  return (
    <div className="rojmel-pane rojmel-pane--daybook">
      <div className="rojmel-pane-title">
        <span>Day Book</span>
      </div>
      <div className="rojmel-pane-body">
        <table className="table rojmel-table rojmel-daybook-table mb-0">
          <thead>
            <tr>
              <th>Today</th>
              <th className="rojmel-num">Summary</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={2} className="rojmel-empty">
                  Loading…
                </td>
              </tr>
            ) : !hasSummary ? (
              <tr>
                <td colSpan={2} className="rojmel-empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              DAY_BOOK_ROWS.map((row) => {
                const raw = readDayBookField(summary, row);
                const display =
                  row.kind === "gm_amount"
                    ? formatGmAndAmount(raw)
                    : formatAmount(raw);
                return (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className="rojmel-num">{display}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function RojmelPane({ title, variant, rows, columns, loading, emptyMessage }) {
  const colSpan = 4 + columns.length;
  const countLabel = loading
    ? "Loading…"
    : `${rows.length} ${rows.length === 1 ? "entry" : "entries"}`;

  return (
    <div className={`rojmel-pane rojmel-pane--${variant}`}>
      <div className="rojmel-pane-title">
        <span>{title}</span>
        <span className="rojmel-pane-count">{countLabel}</span>
      </div>
      <div className="rojmel-pane-body">
        <table className="table rojmel-table mb-0">
          <thead>
            <tr>
              <th className="rojmel-sr">Sr</th>
              <th>Book</th>
              <th>VchNo</th>
              <th>Name</th>
              {columns.map((col) => (
                <th key={col.key} className="rojmel-num">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="rojmel-empty">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="rojmel-empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const opening = isOpeningBalanceRow(row);
                return (
                  <tr
                    key={`${row.book || "r"}-${row.vch_no || idx}-${row.sr || idx}`}
                    className={opening ? "rojmel-opbal" : undefined}
                  >
                    <td className="rojmel-sr">{row.sr ?? idx + 1}</td>
                    <td className="rojmel-book">{row.book || "-"}</td>
                    <td>{row.vch_no || "-"}</td>
                    <td>{row.name || "-"}</td>
                    {columns.map((col) => (
                      <td key={col.key} className="rojmel-num">
                        {col.format(row[col.key])}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RojmelStat({ label, value, tone }) {
  return (
    <div className={`rojmel-stat${tone ? ` rojmel-stat--${tone}` : ""}`}>
      <span className="rojmel-stat-label">{label}</span>
      <span className="rojmel-stat-value">{value}</span>
    </div>
  );
}

function Rojmel() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const sessionBranchId = getSessionBranchId();
  const branches = useMemo(
    () => branchOptionsFromSession(sessionBranchId),
    [sessionBranchId],
  );

  const [date, setDate] = useState(todayIsoDate);
  const [branchId, setBranchId] = useState(() =>
    sessionBranchId != null
      ? String(sessionBranchId)
      : branches[0]?.value || "",
  );

  useEffect(() => {
    const sync = () => {
      const id = getSessionBranchId();
      if (id != null) setBranchId(String(id));
    };
    window.addEventListener("permissionsUpdated", sync);
    return () => window.removeEventListener("permissionsUpdated", sync);
  }, []);
  const [mode, setMode] = useState("today_total");
  const [loading, setLoading] = useState(false);
  const [jama, setJama] = useState([]);
  const [udhar, setUdhar] = useState([]);
  const [totals, setTotals] = useState(null);
  const [dayBookSummary, setDayBookSummary] = useState(null);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const isDayBook = mode === "today_total";

  const columns = useMemo(() => valueColumnsForMode(mode), [mode]);

  const fetchRojmel = useCallback(async ({ silent = false } = {}) => {
    if (!date) {
      if (!silent) toast.error("Please select a date");
      return;
    }
    if (!branchId) {
      if (!silent) toast.error("Please select a branch");
      return;
    }

    setLoading(true);
    try {
      const payload = await postRojmel({
        type: mode,
        date,
        branch_id: Number(branchId),
      });

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        if (!silent) {
          toast.error(payload?.message || "Failed to fetch Rojmel");
        }
        setJama([]);
        setUdhar([]);
        setTotals(null);
        setDayBookSummary(null);
        setOpeningBalance(0);
        setHasFetched(true);
        return;
      }

      const data = payload.data || payload;
      if (mode === "today_total") {
        const summary =
          data.summary && typeof data.summary === "object"
            ? data.summary
            : data;
        setDayBookSummary(summary);
        setJama([]);
        setUdhar([]);
        setTotals(null);
        setOpeningBalance(0);
      } else {
        setDayBookSummary(null);
        setJama(Array.isArray(data.jama) ? data.jama : []);
        setUdhar(Array.isArray(data.udhar) ? data.udhar : []);
        setTotals(data.totals || null);
        setOpeningBalance(
          num(data.opening_balance ?? data.totals?.opening_balance),
        );
      }
      setHasFetched(true);
    } catch (error) {
      if (!silent) {
        toast.error(getErpRequestErrorMessage(error, "Failed to fetch Rojmel"));
      }
      setJama([]);
      setUdhar([]);
      setTotals(null);
      setDayBookSummary(null);
      setOpeningBalance(0);
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, [branchId, date, mode]);

  const fetchRef = useRef(fetchRojmel);
  fetchRef.current = fetchRojmel;

  // One fetch when date / branch / mode change (not on every callback identity churn).
  useEffect(() => {
    fetchRef.current();
  }, [date, branchId, mode]);

  useEffect(() => {
    const handleApiUrlChange = () => {
      fetchRef.current({ silent: true });
    };
    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () =>
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
  }, []);

  const emptyMessage = hasFetched
    ? "No entries for this date"
    : "Select filters to load Rojmel";

  const footerTotals = useMemo(() => {
    const t = totals || {};
    const jamaAmount = num(t.jama_amount);
    const udharAmount = num(t.udhar_amount);
    const udharPayments = num(t.udhar_payments);
    const closingAmount =
      t.closing_amount != null && t.closing_amount !== ""
        ? num(t.closing_amount)
        : openingBalance + jamaAmount - (udharPayments || udharAmount);
    return {
      jamaAmount,
      udharAmount,
      udharPayments,
      closingAmount,
      jamaGold: num(t.jama_gold_fine),
      udharGold: num(t.udhar_gold_fine),
      closingGold: num(t.closing_gold_fine),
      jamaSilver: num(t.jama_silver_fine),
      udharSilver: num(t.udhar_silver_fine),
      closingSilver: num(t.closing_silver_fine),
      opening: num(t.opening_balance ?? openingBalance),
    };
  }, [openingBalance, totals]);

  const handleExportCsv = () => {
    if (!jama.length && !udhar.length) {
      toast.error("No rows to export");
      return;
    }
    const header = [
      "Side",
      "Sr",
      "Book",
      "VchNo",
      "Name",
      "Amount",
      "Gold Fine",
      "Silver Fine",
      "Event Time",
      "Source",
      "Source Id",
      "Remarks",
    ];
    const lines = [header.join(",")];
    const pushRows = (side, rows) => {
      rows.forEach((row, idx) => {
        lines.push(
          [
            side,
            row.sr ?? idx + 1,
            row.book,
            row.vch_no,
            row.name,
            row.amount,
            row.gold_fine,
            row.silver_fine,
            row.event_time,
            row.source,
            row.source_id,
            row.remarks,
          ]
            .map(csvEscape)
            .join(","),
        );
      });
    };
    pushRows("JAMA", jama);
    pushRows("UDHAR", udhar);
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rojmel_${date}_${mode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="border-bottom title-part-padding d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">Rojmel (Day Book)</h4>
            <div className="rojmel-actions d-flex gap-2">
              {/* <button
                type="button"
                className={`btn btn-outline-secondary btn-sm ${buttonClass}`}
                onClick={handleExportCsv}
                disabled={loading || (!jama.length && !udhar.length)}
                title="Export Excel (CSV)"
              >
                <Icon icon="solar:file-download-linear" className="me-1" />
                Excel
              </button> */}
              {/* <button
                type="button"
                className={`btn btn-outline-secondary btn-sm ${buttonClass}`}
                onClick={() => window.print()}
                disabled={loading || (!jama.length && !udhar.length)}
                title="Print"
              >
                <Icon icon="solar:printer-linear" className="me-1" />
                Print
              </button> */}
            </div>
          </div>

          <div className="card-body rojmel-page">
            <div className="rojmel-filters row align-items-end mb-3">
              <div className="col-auto mb-2">
                <label className="form-label mb-1">Date</label>
                <input
                  type="date"
                  className={`form-control form-control-sm ${buttonClass}`}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="col-auto mb-2">
                <label className="form-label mb-1">Branch</label>
                <select
                  className={`form-select form-select-sm ${buttonClass}`}
                  value={branchId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setBranchId(next);
                    const n = Number(next);
                    if (Number.isFinite(n) && n > 0) setActiveBranchId(n);
                  }}
                  style={{ minWidth: 180 }}
                >
                  {branches.length === 0 ? (
                    <option value={branchId || ""}>
                      {branchId ? `Branch #${branchId}` : "No branch"}
                    </option>
                  ) : (
                    branches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="col-auto mb-2">
                {/* <label className="form-label mb-1 d-block">Mode</label> */}
                <div className="rojmel-mode-btns d-flex flex-wrap" role="group">
                  {MODES.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      className={`btn btn-sm ${buttonClass} ${
                        mode === m.key ? "active" : "rojmel-mode-inactive"
                      }`}
                      onClick={() => setMode(m.key)}
                      disabled={loading}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {isDayBook ? (
              <div className="rojmel-daybook-wrap mb-3">
                <DayBookSummaryTable
                  summary={dayBookSummary}
                  loading={loading}
                  emptyMessage={emptyMessage}
                />
              </div>
            ) : (
              <div className="rojmel-grid mb-3">
                <RojmelPane
                  title="Jama"
                  variant="jama"
                  rows={jama}
                  columns={columns}
                  loading={loading}
                  emptyMessage={emptyMessage}
                />
                <RojmelPane
                  title="Udhar"
                  variant="udhar"
                  rows={udhar}
                  columns={columns}
                  loading={loading}
                  emptyMessage={emptyMessage}
                />
              </div>
            )}

            {/* <div className="rojmel-footer">
              <div className="rojmel-footer-title">Day Summary</div>
              <div className="rojmel-footer-grid">
                {(mode === "day_work" ||
                  mode === "today_total" ||
                  mode === "amount") && (
                  <>
                    <RojmelStat
                      label="Opening Balance"
                      value={formatAmount(footerTotals.opening)}
                      tone="opening"
                    />
                    <RojmelStat
                      label="Jama Amount"
                      value={formatAmount(footerTotals.jamaAmount)}
                      tone="jama"
                    />
                    <RojmelStat
                      label="Udhar Amount"
                      value={formatAmount(footerTotals.udharAmount)}
                      tone="udhar"
                    />
                    <RojmelStat
                      label="Closing Balance"
                      value={formatAmount(footerTotals.closingAmount)}
                      tone="closing"
                    />
                  </>
                )}
                {(mode === "day_work" ||
                  mode === "today_total" ||
                  mode === "gold_fine") && (
                  <>
                    <RojmelStat
                      label="Jama Gold Fine"
                      value={formatFine(footerTotals.jamaGold)}
                      tone="jama"
                    />
                    <RojmelStat
                      label="Udhar Gold Fine"
                      value={formatFine(footerTotals.udharGold)}
                      tone="udhar"
                    />
                    <RojmelStat
                      label="Closing Gold Fine"
                      value={formatFine(footerTotals.closingGold)}
                      tone="closing"
                    />
                  </>
                )}
                {(mode === "day_work" ||
                  mode === "today_total" ||
                  mode === "silver_fine") && (
                  <>
                    <RojmelStat
                      label="Jama Silver Fine"
                      value={formatFine(footerTotals.jamaSilver)}
                      tone="jama"
                    />
                    <RojmelStat
                      label="Udhar Silver Fine"
                      value={formatFine(footerTotals.udharSilver)}
                      tone="udhar"
                    />
                    <RojmelStat
                      label="Closing Silver Fine"
                      value={formatFine(footerTotals.closingSilver)}
                      tone="closing"
                    />
                  </>
                )}
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Rojmel;
